import type { Action } from './types';
import { describeIndex, elementForIndex } from './extractor';
import { textOf } from './dom';
import { highlightTarget } from './highlighter';
import { isPrivate } from './redact';
import { markSelfNavigation } from './persist';
import { log } from './logger';

export class ActionError extends Error {}

let extraPrivateSelectors: readonly string[] = [];

export function configureExecutor(redactSelectors: readonly string[]): void {
  extraPrivateSelectors = redactSelectors;
}

function requireElement(index: number): Element {
  const node = elementForIndex(index);
  if (!node) throw new ActionError(`Element ${index} is no longer on the page.`);
  if (isPrivate(node, extraPrivateSelectors)) {
    throw new ActionError(`Element ${index} is in a restricted area and cannot be used.`);
  }
  return node;
}

function setNativeValue(element: HTMLElement, value: string): void {
  const prototype = Object.getPrototypeOf(element) as object;
  const descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');
  if (descriptor?.set) {
    descriptor.set.call(element, value);
    return;
  }
  (element as HTMLInputElement).value = value;
}

function fireInputAndChange(element: Element): void {
  element.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  element.dispatchEvent(new Event('change', { bubbles: true, composed: true }));
}

type PointerLikeInit = MouseEventInit & {
  pointerId?: number;
  pointerType?: string;
  isPrimary?: boolean;
};

function pointerEvent(type: string, init: PointerLikeInit): Event {
  if (typeof PointerEvent === 'function') return new PointerEvent(type, init);
  return new MouseEvent(type, init);
}

function performClick(element: Element): void {
  const rect = element.getBoundingClientRect();
  const init: PointerLikeInit = {
    bubbles: true,
    cancelable: true,
    composed: true,
    view: window,
    clientX: rect.left + rect.width / 2,
    clientY: rect.top + rect.height / 2,
    button: 0,
    buttons: 1,
    pointerId: 1,
    pointerType: 'mouse',
    isPrimary: true,
  };

  const target = element as HTMLElement;
  target.focus?.({ preventScroll: true });
  element.dispatchEvent(pointerEvent('pointerover', init));
  element.dispatchEvent(new MouseEvent('mouseover', init));
  element.dispatchEvent(pointerEvent('pointerdown', init));
  element.dispatchEvent(new MouseEvent('mousedown', init));
  element.dispatchEvent(pointerEvent('pointerup', { ...init, buttons: 0 }));
  element.dispatchEvent(new MouseEvent('mouseup', { ...init, buttons: 0 }));

  if (typeof target.click === 'function') target.click();
  else element.dispatchEvent(new MouseEvent('click', init));
}

function typeInto(index: number, text: string): string {
  const node = requireElement(index);
  const target = node as HTMLElement;
  const fillable =
    node instanceof HTMLInputElement ||
    node instanceof HTMLTextAreaElement ||
    target.isContentEditable;
  if (!fillable) throw new ActionError(`Element ${index} is not a text field.`);
  if ((node as HTMLInputElement).disabled || (node as HTMLInputElement).readOnly) {
    throw new ActionError(`Element ${index} is not editable.`);
  }

  target.focus?.({ preventScroll: true });
  if (target.isContentEditable) {
    target.textContent = text;
  } else {
    setNativeValue(target, text);
  }
  fireInputAndChange(node);
  return `Typed into ${describeIndex(index)}.`;
}

function scrollPage(direction: 'up' | 'down' | 'top' | 'bottom'): string {
  const step = Math.round(window.innerHeight * 0.8);
  const behavior: ScrollBehavior = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    ? 'auto'
    : 'smooth';

  if (direction === 'top') {
    window.scrollTo({ top: 0, behavior });
    return 'Scrolled to the top of the page.';
  }
  if (direction === 'bottom') {
    window.scrollTo({ top: document.body.scrollHeight, behavior });
    return 'Scrolled to the bottom of the page.';
  }
  window.scrollBy({ top: direction === 'down' ? step : -step, behavior });
  return `Scrolled ${direction}.`;
}

function navigate(route: string): string {
  if (!route.startsWith('/') || route.startsWith('//')) {
    throw new ActionError('Only in-app routes beginning with "/" are allowed.');
  }
  const resolved = new URL(route, window.location.origin);
  if (resolved.origin !== window.location.origin) {
    throw new ActionError('Navigation outside this application is not allowed.');
  }

  markSelfNavigation();
  window.location.assign(resolved.pathname + resolved.search + resolved.hash);
  return `Navigated to ${resolved.pathname}.`;
}

function fillForm(fields: { index: number; text: string }[]): string {
  if (fields.length === 0) throw new ActionError('No fields supplied.');
  const filled: string[] = [];
  for (const field of fields) {
    typeInto(field.index, field.text);
    const node = elementForIndex(field.index);
    filled.push(node ? textOf(node) || `field ${field.index}` : `field ${field.index}`);
  }
  return `Filled ${filled.length} field(s): ${filled.join(', ')}.`;
}

function formFor(node: Element): HTMLFormElement | null {
  if (node instanceof HTMLButtonElement || node instanceof HTMLInputElement) {
    const type = node.type.toLowerCase();
    if (type !== 'submit' && type !== 'image') return null;
    return node.form;
  }
  return null;
}

function submitInBackground(form: HTMLFormElement, submitter: Element): void {
  const method = (form.method || 'get').toUpperCase();
  const action = form.action || window.location.href;
  const data = new FormData(form, submitter instanceof HTMLElement ? submitter : undefined);

  const url = new URL(action, window.location.href);
  const init: RequestInit = { method, credentials: 'same-origin', redirect: 'follow' };
  if (method === 'GET') {
    for (const [key, value] of data.entries()) {
      if (typeof value === 'string') url.searchParams.set(key, value);
    }
  } else {
    init.body = data;
  }

  void fetch(url.toString(), init)
    .then((response) => {
      // The submit is what changes server state, so the page has to be re-read
      // to show it. Done here rather than by letting the browser submit natively
      // so the execute result reaches the agent before the page tears down, and
      // so the reload is marked as ours and not announced as a page change.
      markSelfNavigation();
      const target = response.url || window.location.href;
      if (target === window.location.href) window.location.reload();
      else window.location.assign(target);
    })
    .catch((error) => log.warn('background submit failed', error));
}

function clickIndex(index: number): string {
  const node = requireElement(index);
  if ((node as HTMLButtonElement).disabled) {
    throw new ActionError(`Element ${index} is disabled.`);
  }
  const label = describeIndex(index);
  const form = formFor(node);

  if (!form) {
    performClick(node);
    return `Clicked ${label}.`;
  }

  let intercepted = false;
  const onSubmit = (event: Event) => {
    event.preventDefault();
    intercepted = true;
    submitInBackground(form, node);
  };
  form.addEventListener('submit', onSubmit, { capture: true, once: true });
  try {
    performClick(node);
  } finally {
    form.removeEventListener('submit', onSubmit, { capture: true });
  }

  return intercepted
    ? `Clicked ${label}. Submitted; reloading the page to show the result.`
    : `Clicked ${label}.`;
}

export function execute(action: Action): string {
  log.debug('execute', action);
  switch (action.kind) {
    case 'click':
      return clickIndex(action.index);
    case 'type':
      return typeInto(action.index, action.text);
    case 'scroll':
      return scrollPage(action.direction);
    case 'navigate':
      return navigate(action.route);
    case 'highlight':
      return highlightTarget(action.index);
    case 'fill_form':
      return fillForm(action.fields);
    default: {
      const kind = (action as { kind?: string }).kind ?? 'unknown';
      throw new ActionError(`Unsupported action "${kind}".`);
    }
  }
}
